/**
 * CustomerSitePage — entrypoint for any /sites/:slug or custom-domain customer site.
 *
 * Resolution order:
 *   1. URL param :slug (preferred, used for /sites/:slug)
 *   2. Hostname match against SITE_REGISTRY[*].custom_domain (custom domain hosting)
 *
 * Once we have a config, we hydrate any backend overrides (phone, theme, content)
 * via /api/website-config/by-slug/{slug}, then render the chosen template.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { getSiteBySlug, getSiteByDomain } from './registry';
import { getTemplate } from './templates';
import SectionRenderer from '../components/website/SectionRenderer';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CustomerSitePage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get('preview_token') || '';
  const initialConfig = useMemo(() => {
    if (params?.slug) return getSiteBySlug(params.slug);
    if (typeof window !== 'undefined') {
      const byHost = getSiteByDomain(window.location.hostname);
      if (byHost) return byHost;
    }
    return null;
  }, [params?.slug]);

  const [config, setConfig] = useState(initialConfig);
  const [hydrated, setHydrated] = useState(false);
  // Template-driven (new) site payload
  const [templateSite, setTemplateSite] = useState(null);
  const [tplLoaded, setTplLoaded] = useState(false);
  const [pausedSite, setPausedSite] = useState(null);

  // Hydrate static-config flow (existing customer sites like Evolve Drywall)
  useEffect(() => {
    if (!initialConfig?.slug) {
      setHydrated(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API}/api/website-config/by-slug/${initialConfig.slug}`);
        if (!resp.ok) return;
        const remote = await resp.json();
        if (cancelled) return;
        setConfig((current) => mergeConfig(current, remote));
      } catch {
        /* keep static config */
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialConfig?.slug]);

  // Fallback: if no static config matches, try the new template-based system
  useEffect(() => {
    if (initialConfig || !params?.slug) {
      setTplLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const qs = previewToken ? `?preview_token=${encodeURIComponent(previewToken)}` : '';
        const r = await fetch(`${API}/api/customer-websites/by-slug/${params.slug}/full${qs}`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        if (data?.paused) {
          setPausedSite(data);
        } else if (data?.template_snapshot && data?.content_json) {
          setTemplateSite(data);
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setTplLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [initialConfig, params?.slug, previewToken]);

  // 0) Subscription paused — render pause screen, do not leak content
  if (pausedSite) {
    return <PausedSite site={pausedSite} />;
  }

  // 1) Template-based site (new system)
  if (templateSite) {
    const seoTitle = `${templateSite.business_name || templateSite.content_json?.branding?.business_name || templateSite.slug}`;
    const isPreview = templateSite.preview_mode === true;
    return (
      <>
        <Helmet>
          <title>{isPreview ? `[Private preview] ${seoTitle}` : seoTitle}</title>
          <meta name="description"
                content={templateSite.content_json?.hero?.subheadline || ''} />
          <meta property="og:title" content={seoTitle} />
          {isPreview && <meta name="robots" content="noindex,nofollow" />}
        </Helmet>
        {isPreview && <PrivatePreviewBanner status={templateSite.preview_subscription_status} />}
        <SectionRenderer
          snapshot={templateSite.template_snapshot}
          content={templateSite.content_json}
          slug={templateSite.slug}
        />
      </>
    );
  }

  // 2) Legacy static-config sites (Evolve etc.)
  if (config) {
    const template = getTemplate(config.template_id);
    if (!template?.component) {
      return <ComingSoon templateId={config.template_id} />;
    }
    const TemplateComponent = template.component;
    return <TemplateComponent config={config} hydrated={hydrated} />;
  }

  // 3) Still loading template-based fallback
  if (!tplLoaded) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  return <NotFound slug={params?.slug} />;
}

function mergeConfig(staticCfg, remote) {
  if (!remote) return staticCfg;
  return {
    ...staticCfg,
    business_name: remote.business_name || staticCfg.business_name,
    phone_number: remote.phone_number || staticCfg.phone_number,
    custom_domain: remote.custom_domain || staticCfg.custom_domain,
    template_id: remote.template_id || staticCfg.template_id,
    industry: remote.industry || staticCfg.industry,
    seo: { ...(staticCfg.seo || {}), ...(remote.seo_settings || {}) },
    brand: { ...(staticCfg.brand || {}), ...(remote.theme_settings || {}) },
  };
}

function NotFound({ slug }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 text-center">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">404</p>
        <h1 className="text-3xl font-black text-slate-900">Site not found</h1>
        <p className="mt-3 text-slate-600 max-w-md">
          {slug ? <>No published Agent9 customer site exists at the slug <code className="px-1.5 py-0.5 bg-slate-200 rounded">{slug}</code>.</> : 'This domain has not been mapped to a published Agent9 customer site.'}
        </p>
        <a href="/" className="inline-block mt-6 text-sm font-semibold text-sky-600 hover:underline">
          Go to Agent9 →
        </a>
      </div>
    </div>
  );
}

function PrivatePreviewBanner({ status }) {
  return (
    <div
      data-testid="customer-site-preview-banner"
      className="sticky top-0 z-50 w-full bg-amber-500 text-amber-950 text-sm font-medium px-4 py-2 text-center shadow-md"
      role="status"
    >
      <span className="inline-flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full bg-amber-900 animate-pulse" />
        Private preview — this site is not publicly visible.
        {status && (
          <span className="hidden sm:inline opacity-75">
            (subscription status: {String(status).replace(/_/g, ' ')})
          </span>
        )}
      </span>
    </div>
  );
}

function PausedSite({ site }) {
  return (
    <div
      data-testid="customer-site-paused"
      className="min-h-screen flex items-center justify-center bg-slate-50 px-6 text-center"
    >
      <Helmet>
        <title>{site?.business_name || 'Website paused'}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="max-w-md">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
          Temporarily unavailable
        </p>
        <h1 className="text-3xl font-black text-slate-900">
          This website is temporarily paused
        </h1>
        <p className="mt-3 text-slate-600">
          {site?.message || 'The owner can restore it at any time from their Agent9 dashboard.'}
        </p>
      </div>
    </div>
  );
}

function ComingSoon({ templateId }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 text-center">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
          Template
        </p>
        <h1 className="text-3xl font-black text-slate-900">Coming soon</h1>
        <p className="mt-3 text-slate-600 max-w-md">
          The <strong>{templateId}</strong> template is reserved for the upcoming Agent9 Website Builder release.
        </p>
      </div>
    </div>
  );
}
